import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { BusyIcon } from '../../components/loading';
import { StepHint } from '../../components/StepHint/StepHint';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { FormTitle } from '../../components/FormTitle';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { CellContextMenu, canBulkCopy } from '../../components/gridEdit';
import { fmtLocalDate } from '../../utils/datetime';
import { PREVIEW_AS_TOKEN, openReportPreview } from '../../utils/reportPreview';
import { useAuthStore } from '../../auth/store';
import { useNavActions } from '../../hooks/useNavActions';
import { ReportProgressOverlay } from './ReportProgressOverlay';
import '../backlog/backlog.css';

/**
 * Batch Reporting — Class D workflow (New Changes in Desgin →
 * Reporting-Module-Prototype.html #screenBatch).
 *
 * Query many Jobs at once → assign a report template per row → Preview → Save
 * mints a race-safe ReportID per ticked+templated row into the SAME pipeline
 * Custom Reporting feeds (Report Validation).  Ports the legacy XAF
 * BatchReportingViewController — but the ID is minted by the race-safe
 * dbo.AI_Reporting_SaveReport (§14a Rule B), never the legacy MAX+1 race
 * (BatchReportingViewController.cs line 2207).
 *
 * Standard notes:
 *  • Dynamic Island stepper (Query → Retrieve → Preview → Save) docked in the
 *    top bar (§5); a <StepHint> narrates the active step (§5).
 *  • Audit Trail chip on the title row via <FormTitle auditFormName> (§8).
 *  • Project Notes is DELIBERATELY omitted (§8 documented exemption): a batch
 *    query spans many jobs/clients, so there is no single owning record.
 *  • Save runs through the shared confirm; results/errors go through useNotify.
 */

interface LookupItem { id: string; name: string }
interface ClientItem { id: string; name: string; address: string; phone: string }
interface ProjectItem { id: string; projectId: string; projectName: string; clientOid: string | null }
interface MatrixItem { id: string; name: string; matrixOid: string | null }
interface TestItem { id: string; name: string; matrixOid: string | null; methodNumber: string }

interface BatchLookups {
  clients: ClientItem[];
  projects: ProjectItem[];
  matrices: MatrixItem[];
  tests: TestItem[];
  categories: LookupItem[];
}

// ── Multi-column dropdown lookup (GoLive-style — drops below the field, paged 20) ──
interface PopupCol<T> { label: string; width: number; value: (row: T) => string }

const PAGE_SIZE = 20;

function LookupDropdown<T extends { id: string; name: string }>({
  anchorRect, rows, cols, selectedId, onSelect, onClose,
}: {
  anchorRect: DOMRect;
  rows: T[];
  cols: PopupCol<T>[];
  selectedId: string;
  onSelect: (id: string, name: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState<Record<number, string>>({});
  const [page, setPage]     = useState(1);

  const filtered = rows.filter(r =>
    cols.every((c, i) => !search[i] || c.value(r).toLowerCase().includes(search[i].toLowerCase())));
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalWidth = cols.reduce((s, c) => s + c.width, 0) + 2;

  // Reset to page 1 whenever search changes
  const handleSearch = (i: number, v: string) => {
    setSearch(p => ({ ...p, [i]: v }));
    setPage(1);
  };

  // Position: drop below the anchor; flip up if not enough space
  const dropTop  = anchorRect.bottom + 2;
  const dropLeft = anchorRect.left;
  const maxH     = Math.min(360, window.innerHeight - dropTop - 8);

  return (
    <>
      {/* Click-away backdrop */}
      <div style={{ position:'fixed', inset:0, zIndex:1999 }} onClick={onClose} />
      <div style={{
        position:'fixed', zIndex:2000,
        top: dropTop, left: dropLeft,
        width: Math.max(totalWidth, anchorRect.width),
        background:'#fff', border:'1px solid #bbb',
        borderRadius:3, boxShadow:'0 4px 16px rgba(0,0,0,.18)',
        display:'flex', flexDirection:'column',
      }}>
        {/* Column headers + per-column search */}
        <div style={{ borderBottom:'2px solid #d0d4db', background:'#f3f4f6' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
            <colgroup>{cols.map((c,i) => <col key={i} style={{ width:c.width }} />)}</colgroup>
            <thead>
              <tr>
                {cols.map((c, i) => (
                  <th key={i} style={{ padding:'4px 7px 2px', textAlign:'left', fontWeight:700, fontSize:11, color:'#374151' }}>
                    {c.label}
                  </th>
                ))}
              </tr>
              <tr>
                {cols.map((_c, i) => (
                  <th key={i} style={{ padding:'1px 5px 4px' }}>
                    <input value={search[i] ?? ''} onChange={e => handleSearch(i, e.target.value)}
                           placeholder=""
                           style={{ width:'100%', padding:'2px 4px', fontSize:11, border:'1px solid #ccc', borderRadius:2 }} />
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>

        {/* Rows */}
        <div style={{ overflowY:'auto', maxHeight: maxH }}>
          <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed', fontSize:12 }}>
            <colgroup>{cols.map((c,i) => <col key={i} style={{ width:c.width }} />)}</colgroup>
            <tbody>
              {pageRows.map(r => (
                <tr key={r.id}
                    onClick={() => { onSelect(r.id, r.name); onClose(); }}
                    style={{ cursor:'pointer', background: r.id === selectedId ? '#dbeafe' : undefined }}
                    onMouseEnter={e => { if (r.id !== selectedId) (e.currentTarget as HTMLTableRowElement).style.background = '#f0f4ff'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = r.id === selectedId ? '#dbeafe' : ''; }}>
                  {cols.map((c, i) => (
                    <td key={i} style={{ padding:'4px 7px', borderBottom:'1px solid #f0f0f0',
                                         overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
                      {c.value(r)}
                    </td>
                  ))}
                </tr>
              ))}
              {pageRows.length === 0 && (
                <tr><td colSpan={cols.length} style={{ padding:10, textAlign:'center', color:'#999', fontSize:12 }}>No results</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer — matches GoLive "Page 1 of 14 (278 items)  Page size: 20" */}
        <div style={{ borderTop:'1px solid #e5e7eb', padding:'4px 8px', display:'flex',
                       alignItems:'center', justifyContent:'space-between', background:'#fafbfc' }}>
          <span style={{ fontSize:11, color:'#6b7280' }}>
            Page {page} of {totalPages} ({filtered.length} item{filtered.length !== 1 ? 's' : ''})
          </span>
          <div style={{ display:'flex', alignItems:'center', gap:3 }}>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const p = totalPages <= 7 ? i + 1
                : i === 0 ? 1
                : i === 6 ? totalPages
                : page <= 4 ? i + 1
                : page >= totalPages - 3 ? totalPages - 6 + i
                : page - 3 + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                        style={{ minWidth:20, padding:'1px 4px', fontSize:11, border:'1px solid #d1d5db',
                                 borderRadius:2, cursor:'pointer',
                                 background: p === page ? 'var(--ai-orange, #e8670a)' : '#fff',
                                 color: p === page ? '#fff' : '#374151', fontWeight: p === page ? 700 : 400 }}>
                  {p}
                </button>
              );
            })}
            <span style={{ fontSize:11, color:'#6b7280', marginLeft:6 }}>Page size: {PAGE_SIZE}</span>
          </div>
        </div>
      </div>
    </>
  );
}

// Trigger field — shows selected value; clicking anywhere opens the dropdown.
function LookupField({ label, value, disabled, onOpen, onClear }:
  { label: string; value: string; disabled: boolean;
    onOpen: (rect: DOMRect) => void; onClear: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const handleOpen = () => {
    if (disabled || !ref.current) return;
    onOpen(ref.current.getBoundingClientRect());
  };
  return (
    <div ref={ref} style={{ display:'flex', alignItems:'center', gap:2, height:26 }}>
      <div onClick={handleOpen}
           style={{ flex:1, border:'1px solid #ccc', borderRadius:2, padding:'2px 6px', fontSize:12,
                    background: disabled ? '#f5f5f5' : '#fff', color: value ? '#1f2430' : '#aaa',
                    cursor: disabled ? 'default' : 'pointer', minHeight:22, lineHeight:'18px',
                    overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
        {value || 'N/A'}
      </div>
      {value && !disabled && (
        <button onClick={onClear} title={`Clear ${label}`}
                style={{ border:'none', background:'none', cursor:'pointer', color:'#aaa', fontSize:13, padding:'0 2px', lineHeight:1 }}>✕</button>
      )}
      <button onClick={handleOpen} disabled={disabled} title={`Pick ${label}`}
              style={{ border:'1px solid #ccc', background:'#f0f0f0', borderRadius:2,
                       cursor: disabled ? 'default' : 'pointer', padding:'1px 5px', fontSize:11, lineHeight:'18px' }}>▼</button>
    </div>
  );
}

interface PackageItem { oid: string; packageName: string | null; reportName: string | null; sort: number }

interface BatchJobRow {
  id: string;
  jobId: string | null;
  jobOid: string | null;
  client: string | null;
  projectId: string | null;
  projectName: string | null;
  dateReceived: string | null;
  dueDate: string | null;
  dateReported: string | null;
  reportedBy: string | null;
  testName: string | null;
  sampleCategory: string | null;
  sampleMatrix: string | null;
  /** Per-row template selection (packageOid) — the user assigns it in the grid. */
  packageOid?: string;
  /** Whether this row has been previewed at least once (gates Save). */
  previewed?: boolean;
}

export function BatchReportingPage() {
  const notify = useNotify();
  const confirm = useConfirm();
  // MAC: a batch Save mints reports, so it needs Create — hidden (never greyed)
  // without it. Load / Clear / Preview / Refresh are reads and stay.
  const perm = useNavActions('batchreport');
  const currentUserName = useAuthStore(s => s.user?.name ?? '');
  const [loadMs, setLoadMs] = useState<number | null>(null);

  // ── Query panel state ─────────────────────────────────────────────────
  const [lookups, setLookups] = useState<BatchLookups | null>(null);
  const [packages, setPackages] = useState<PackageItem[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clientOid, setClientOid] = useState('');
  const [clientName, setClientName] = useState('');
  const [projectOid, setProjectOid] = useState('');
  const [projectName, setProjectName] = useState('');
  const [matrixOid, setMatrixOid] = useState('');
  const [categoryOid, setCategoryOid] = useState('');
  const [testOid, setTestOid] = useState('');
  const [testName, setTestName] = useState('');
  const [all, setAll] = useState(false);
  // Popup anchor rects (null = closed)
  const [clientPopup,  setClientPopup]  = useState<DOMRect | null>(null);
  const [projectPopup, setProjectPopup] = useState<DOMRect | null>(null);
  const [testPopup,    setTestPopup]    = useState<DOMRect | null>(null);

  // ── Results state ─────────────────────────────────────────────────────
  const [rows, setRows] = useState<BatchJobRow[]>([]);
  const [retrieved, setRetrieved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);
  const renderAbortRef = useRef<AbortController | null>(null);
  const [ctxMenu, setCtxMenu] = useState<{ row: BatchJobRow; x: number; y: number } | null>(null);

  // Cascaded dropdowns (client → projects, matrix → tests) mirror the legacy
  // ImmediatePostData cascades (BatchReportingViewController ObjectChanged).
  const projectOpts = useMemo(() => {
    if (!lookups) return [];
    if (!clientOid) return lookups.projects;
    return lookups.projects.filter(p => p.clientOid === clientOid);
  }, [lookups, clientOid]);
  const testOpts = useMemo(() => {
    if (!lookups) return [];
    const vm = lookups.matrices.find(m => m.id === matrixOid);
    if (!matrixOid || !vm?.matrixOid) return lookups.tests;
    return lookups.tests.filter(t => t.matrixOid === vm.matrixOid);
  }, [lookups, matrixOid]);

  // Load the dropdown payloads + report packages once.
  useEffect(() => {
    const t0 = performance.now();
    void (async () => {
      try {
        const [luRes, pkgRes] = await Promise.all([
          api.get('/api/v1/reporting/batch/lookups'),
          api.get('/api/v1/reporting/custom/report-packages'),
        ]);
        setLookups((luRes.data as { data: BatchLookups }).data);
        setPackages((pkgRes.data as { data: PackageItem[] }).data ?? []);
      } catch {
        notify.error('Could not load the Batch Reporting query lookups.');
      } finally {
        setLoadMs(performance.now() - t0);
      }
    })();
  }, [notify]);

  // Clear a stale project/test when its parent changes and the value no longer fits.
  useEffect(() => {
    if (projectOid && !projectOpts.some(p => p.id === projectOid)) setProjectOid('');
  }, [projectOpts, projectOid]);
  useEffect(() => {
    if (testOid && !testOpts.some(t => t.id === testOid)) setTestOid('');
  }, [testOpts, testOid]);

  // ── Load (Retrieve) — run the query ───────────────────────────────────
  const loadBatch = useCallback(async () => {
    // GoLive pre-flight (BatchReportingViewController.JobIdsRetrieve_Execute
    // lines 435-449 + 577-581).  The date range is BOTH-OR-NEITHER: a partial
    // or inverted range contributes NO criteria in ALPACA, it just raises one
    // of these messages.  Wording is verbatim from the legacy localisation
    // (BTLIMS.Web/Model.xafml LocalizationItem) — including its typos — so the
    // lab sees the identical text it does today.  (Re-ported 2026-08-18.)
    if (!all) {
      if (!dateFrom && dateTo) {
        notify.info("'Date Recevied From' must not be empty. Please enter a valid date range");
        return;
      }
      if (dateFrom && !dateTo) {
        notify.info("'Date Received To' must not be empty. Please enter a valid date range.");
        return;
      }
      if (dateFrom && dateTo && dateFrom > dateTo) {
        notify.info('The received from date  must be less than the received to date.');
        return;
      }
      // Nothing to query on and "All" unticked → legacy sets the grid filter to
      // 1=2 and asks the user to pick something.  Mirrored here so we never
      // fire the unfiltered (slowest possible) query.
      const hasFilter = Boolean(
        (dateFrom && dateTo) || clientOid || projectOid || matrixOid || categoryOid || testOid,
      );
      if (!hasFilter) {
        notify.info('Select atleast one query option to retrieve.');
        setRows([]);
        setSel(new Set());
        setRetrieved(true);
        return;
      }
    }
    setLoading(true);
    const t0 = performance.now();
    try {
      const body = {
        dateReceivedFrom: all ? null : (dateFrom || null),
        dateReceivedTo:   all ? null : (dateTo || null),
        clientOid:        all ? null : (clientOid || null),
        projectOid:       all ? null : (projectOid || null),
        matrixOid:        all ? null : (matrixOid || null),
        categoryOid:      all ? null : (categoryOid || null),
        testOid:          all ? null : (testOid || null),
        all,
      };
      const res = await api.post('/api/v1/reporting/batch/query', body);
      // GoLive parity: Retrieve auto-fills DateReported/ReportedBy with
      // today's date and the signed-in user for EVERY row — a preview of
      // what Save will stamp, not a lookup of the job's prior report (that's
      // why ALPACA shows this even for jobs with no report history yet).
      const now = new Date().toISOString();
      const data = ((res.data as { data: BatchJobRow[] }).data ?? [])
        .map(r => ({
          ...r,
          dateReported: now,
          reportedBy: currentUserName,
          packageOid: undefined,
          previewed: false,
        }));
      setRows(data);
      // Rows start UNticked — the user picks which jobs to act on (ALPACA
      // parity; previously every row was pre-selected on Load).
      setSel(new Set());
      setRetrieved(true);
      setLoadMs(performance.now() - t0);
      // Legacy "noavailabledata" wording, verbatim (Model.xafml).
      if (data.length === 0) notify.info('There is no data available for the given set of queries.');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Query failed.';
      notify.error(msg);
    } finally {
      setLoading(false);
    }
  }, [all, dateFrom, dateTo, clientOid, projectOid, matrixOid, categoryOid, testOid, notify, currentUserName]);

  // ── Clear the whole screen ────────────────────────────────────────────
  const clearScreen = useCallback(() => {
    setDateFrom(''); setDateTo('');
    setClientOid(''); setClientName('');
    setProjectOid(''); setProjectName('');
    setMatrixOid(''); setCategoryOid('');
    setTestOid(''); setTestName('');
    setAll(false);
    setRows([]); setSel(new Set()); setRetrieved(false); setLoadMs(null);
  }, []);

  // ── Per-row template assignment ───────────────────────────────────────
  const setRowTemplate = useCallback((rowId: string, packageOid: string) => {
    setRows(prev => prev.map(r =>
      r.id === rowId ? { ...r, packageOid: packageOid || undefined, previewed: false } : r));
  }, []);

  // Unticking a row's selection checkbox drops it from this batch action —
  // ALPACA parity: its in-progress Template pick and Preview-confirmed tick
  // are cleared along with it, so re-ticking the row later starts clean
  // instead of carrying stale state from before it was deselected.
  const handleSelectionChange = useCallback((next: Set<string | number>) => {
    const deselected = [...sel].filter(id => !next.has(id));
    if (deselected.length > 0) {
      const deselectedSet = new Set(deselected);
      setRows(prev => prev.map(r =>
        deselectedSet.has(r.id) ? { ...r, packageOid: undefined, previewed: false } : r));
    }
    setSel(next);
  }, [sel]);

  // Right-click a Template cell → Copy to All / Copy to Selected (§9).
  const copyTemplateToAll = useCallback((packageOid: string) => {
    setRows(prev => prev.map(r => ({ ...r, packageOid, previewed: false })));
    notify.success('Template copied to all rows.');
  }, [notify]);
  const copyTemplateToSelected = useCallback((packageOid: string) => {
    if (sel.size === 0) { notify.warning('Tick one or more rows first.'); return; }
    setRows(prev => prev.map(r =>
      sel.has(r.id) ? { ...r, packageOid, previewed: false } : r));
    notify.success(`Template copied to ${sel.size} selected row${sel.size === 1 ? '' : 's'}.`);
  }, [sel, notify]);

  // ── Preview one row's job+template → mark it previewed (gates Save) ────
  const previewRow = useCallback(async (row: BatchJobRow) => {
    if (!row.packageOid) { notify.warning('Assign a report template to this row first.'); return; }
    if (!row.jobOid) return;
    setRendering(true);
    renderAbortRef.current?.abort();
    const ac = new AbortController();
    renderAbortRef.current = ac;
    try {
      // deliver=token → open a real, re-fetchable preview URL rather than a
      // blob: URL that dies before the PDF viewer's Download can use it
      // (see utils/reportPreview).
      const res = await api.post(
        `/api/v1/reporting/custom/${encodeURIComponent(row.jobOid)}/render-devx`,
        { selectedParameterOids: [], selectedSubReportOids: [] },
        { params: { packageOid: row.packageOid, ...PREVIEW_AS_TOKEN }, signal: ac.signal });
      if (!openReportPreview(res.data))
        notify.info('Preview ready — allow pop-ups for this site to open it.');
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, previewed: true } : r));
    } catch (e: unknown) {
      if (ac.signal.aborted) return;
      const err = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data;
      notify.error(err?.error?.message ?? 'Preview failed.');
    } finally {
      setRendering(false);
    }
  }, [notify]);

  // ── Save — mint a ReportID per ticked+templated row ───────────────────
  const tickedRows = useMemo(() => rows.filter(r => sel.has(r.id)), [rows, sel]);
  const missingTemplate = tickedRows.some(r => !r.packageOid);
  const anyPreviewed = tickedRows.some(r => r.previewed);
  const canSave = tickedRows.length > 0 && !missingTemplate && anyPreviewed && !saving;

  const saveBatch = useCallback(async () => {
    if (tickedRows.length === 0) { notify.warning('Tick at least one row to save.'); return; }
    if (missingTemplate) { notify.warning('Every ticked row needs a report template.'); return; }
    if (!anyPreviewed) { notify.warning('Preview at least one report before saving.'); return; }
    const ok = await confirm.confirm({
      title: 'Do you want to Save?',
      message: `Generate ${tickedRows.length} report${tickedRows.length === 1 ? '' : 's'} and send ${tickedRows.length === 1 ? 'it' : 'them'} to Report Validation?`,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const res = await api.post('/api/v1/reporting/batch/save', {
        rows: tickedRows.map(r => ({ jobOid: r.jobOid, packageOid: r.packageOid })),
      });
      const d = (res.data as { data?: { savedCount?: number; requestedCount?: number;
        firstReportId?: string; lastReportId?: string } }).data;
      const saved = d?.savedCount ?? 0;
      const req = d?.requestedCount ?? tickedRows.length;
      // Documented §18.6 exception — Batch Reporting save surfaces the minted
      // ReportID(s) (ALPACA "firstReportID … LastReportID" success message).
      if (saved > 0) {
        const idPart = d?.firstReportId
          ? (d.lastReportId && d.lastReportId !== d.firstReportId
              ? `${d.firstReportId} … ${d.lastReportId}`
              : d.firstReportId)
          : '';
        notify.success(
          `${idPart ? idPart + ' — ' : ''}${saved} report${saved === 1 ? '' : 's'} saved successfully.`
          + (saved < req ? ` (${req - saved} could not be saved.)` : ''));
      } else {
        notify.error('No reports were saved — check the selected rows have Pending-Reporting samples.');
      }
      // Refresh the query so saved jobs drop off (their samples left Pending Reporting).
      await loadBatch();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Save failed.';
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  }, [tickedRows, missingTemplate, anyPreviewed, confirm, notify, loadBatch]);

  // ── Dynamic Island stepper (Query → Retrieve → Preview → Save) ────────
  const stepperSteps = useMemo<IslandStep[]>(() => {
    const hasQuery = all || !!(dateFrom || dateTo || clientOid || projectOid || matrixOid || categoryOid || testOid);
    return [
      { id: 'query',    label: 'Query',    state: retrieved ? 'done' : (hasQuery ? 'active' : 'active') },
      { id: 'retrieve', label: 'Retrieve', state: retrieved ? 'done' : (hasQuery ? 'active' : 'todo') },
      { id: 'preview',  label: 'Preview',  state: !retrieved ? 'todo' : (anyPreviewed ? 'done' : 'active') },
      { id: 'save',     label: 'Save',     state: canSave ? 'active' : 'todo' },
    ];
  }, [all, dateFrom, dateTo, clientOid, projectOid, matrixOid, categoryOid, testOid, retrieved, anyPreviewed, canSave]);

  const hint = useMemo(() => {
    if (!retrieved)
      return { tone: 'guidance' as const, node: <><b>Step 1 · Set your query.</b> Narrow by Date Received or Client, or check <b>All</b>, then click <b>Load</b>.</> };
    if (rows.length === 0)
      return { tone: 'info' as const, node: <><b>No matching jobs.</b> Widen the query or check <b>All</b>, then <b>Load</b> again.</> };
    if (missingTemplate)
      return { tone: 'guidance' as const, node: <><b>Step 2 · Assign a Template.</b> Pick a report <b>Template</b> per ticked row (right-click to copy it to all / selected), then <b>Preview</b>.</> };
    if (!anyPreviewed)
      return { tone: 'guidance' as const, node: <><b>Step 3 · Preview.</b> Click a row's <b>Preview</b> to check its report, then <b>Save</b> to send them to Report Validation.</> };
    return { tone: 'complete' as const, node: <><b>Step 4 · Save.</b> Click <b>Save</b> to mint a Report ID per ticked row and send them to Report Validation.</> };
  }, [retrieved, rows.length, missingTemplate, anyPreviewed]);

  // ── Results grid columns ──────────────────────────────────────────────
  const columns = useMemo<AppColumn<BatchJobRow>[]>(() => [
    { field: 'jobId',       header: 'JobID',       width: 120, text: r => r.jobId ?? '' },
    { field: 'client',      header: 'Client',      width: 180, text: r => r.client ?? '' },
    { field: 'projectId',   header: 'ProjectID',   width: 120, text: r => r.projectId ?? '' },
    { field: 'projectName', header: 'ProjectName', width: 200, text: r => r.projectName ?? '' },
    { field: 'dateReceived', header: 'DateReceived', width: 130, type: 'date' },
    { field: 'dueDate',     header: 'DueDate',     width: 120, type: 'date' },
    { field: 'dateReported', header: 'DateReported', width: 130,
      render: r => r.dateReported ? fmtLocalDate(r.dateReported) : <span style={{ color: 'var(--ai-muted)' }}>—</span> },
    { field: 'reportedBy',    header: 'ReportedBy',      width: 140, text: r => r.reportedBy    ?? '' },
    {
      field: 'packageOid', header: 'Template', width: 240, sortable: false, filterable: false,
      render: r => (
        <select
          className="bl-cell-select"
          value={r.packageOid ?? ''}
          onChange={e => setRowTemplate(r.id, e.target.value)}
          onContextMenu={e => {
            if (!canBulkCopy(r.packageOid)) return;   // nothing to copy from an empty cell
            e.preventDefault();
            setCtxMenu({ row: r, x: e.clientX, y: e.clientY });
          }}
          style={{
            width: '100%', fontSize: 11.5, padding: '2px 6px', borderRadius: 3,
            border: r.packageOid ? '1px solid var(--ai-line)' : '1px solid var(--ai-required-line)',
            background: r.packageOid ? '#fff' : 'var(--ai-required-bg)',
          }}
        >
          <option value="">— Select template —</option>
          {packages.map(p => (
            <option key={p.oid} value={p.oid}>{p.packageName ?? p.reportName ?? p.oid}</option>
          ))}
        </select>
      ),
    },
    {
      field: '_preview', header: 'Preview', width: 80, align: 'center', sortable: false, filterable: false,
      render: r => (
        <button
          onClick={() => void previewRow(r)}
          disabled={!r.packageOid || rendering}
          title={!r.packageOid ? 'Assign a template first' : `Preview ${r.jobId}`}
          style={{ background: 'transparent', border: 'none', cursor: r.packageOid ? 'pointer' : 'not-allowed',
                   fontSize: 16, padding: '2px 6px', opacity: r.packageOid ? 1 : 0.4 }}
        >
          {/* Preview = 👁 (§7 — never Load's ⬇️). Green tick once previewed. */}
          {r.previewed
            ? <span title="Previewed" style={{ color: 'var(--ai-green)' }}>👁✓</span>
            : <span aria-hidden="true">👁</span>}
        </button>
      ),
    },
  ], [packages, rendering, setRowTemplate, previewRow]);

  // ── Phase 2.3 · Keyboard shortcuts for the Batch Reporting title-row action
  //    bar (CLAUDE.md standing rule 14, §18.6, ADR-0013).  Each key MIRRORS a
  //    title-row button — same handler, same disabled state — grouped under the
  //    form title.  Registered UNCONDITIONALLY before the return.  (Refresh's
  //    Alt+R is provided by the shared <AppToolbar> onRefresh below.)
  useHotkeys([
    { combo: 'alt+l', label: 'Load',  group: 'Batch Reporting', order: 1, disabled: loading,
      run: () => { void loadBatch(); } },
    { combo: 'mod+s', label: 'Save',  group: 'Batch Reporting', order: 2, disabled: !canSave || !perm.create,
      run: () => { void saveBatch(); } },
    { combo: 'alt+c', label: 'Clear', group: 'Batch Reporting', order: 3,
      run: () => clearScreen() },
  ]);

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Reporting</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Batch Reporting</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="batchreport" loadMs={loadMs} loading={loading}
                       auditFormName="Batch Reporting" />
            {/* Title-row workflow actions (§6): Load · Save · Clear. */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="ca-btn ca-btn-solid" onClick={() => void loadBatch()} disabled={loading}
                      title={`Run the query and list matching jobs (${formatCombo('alt+l')})`}>
                {loading ? <BusyIcon busy icon="ti-download" /> : <span aria-hidden="true">⬇️</span>}
                &nbsp;Load
              </button>
              {perm.create && (
                <button className="ca-btn ca-btn-solid" onClick={() => void saveBatch()} disabled={!canSave}
                        title={(!retrieved ? 'Load jobs first'
                          : tickedRows.length === 0 ? 'Tick at least one row'
                          : missingTemplate ? 'Every ticked row needs a template'
                          : !anyPreviewed ? 'Preview at least one report first'
                          : 'Save the ticked reports to Report Validation') + ` (${formatCombo('mod+s')})`}>
                  {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span aria-hidden="true">💾</span>}
                  &nbsp;Save{tickedRows.length > 0 ? ` (${tickedRows.length})` : ''}
                </button>
              )}
              <button className="ca-btn" onClick={clearScreen}
                      title={`Clear the query, results and selections on this screen (${formatCombo('alt+c')})`}>
                <span className="tb-i tb-i-red" aria-hidden="true">✕</span>&nbsp;Clear
              </button>
            </div>
          </div>
          <div className="dp-subtitle">
            Query many Jobs at once, assign a report template per row, then Save them all to Report Validation.
          </div>
          <TopbarStepper steps={stepperSteps} />
        </div>
      </div>

      <div className="bl-grid-area">
        <StepHint tone={hint.tone}>{hint.node}</StepHint>

        {/* Toolbar (Refresh only — the workflow actions live on the title row). */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar onRefresh={() => void loadBatch()} refreshing={loading} />
        </div>

        {/* Query panel card. */}
        <div className="bl-card" style={{ padding: '14px 16px', marginBottom: 12 }}>
          <div className="br-qform">
            <label>Date Received:</label>
            <div className="br-range">
              <input type="date" value={dateFrom} disabled={all} onChange={e => setDateFrom(e.target.value)} />
              <span className="br-arrow">→</span>
              <input type="date" value={dateTo} disabled={all} onChange={e => setDateTo(e.target.value)} />
            </div>

            <label>Client:</label>
            <LookupField label="Client" value={clientName} disabled={all}
              onOpen={rect => setClientPopup(rect)}
              onClear={() => { setClientOid(''); setClientName(''); setProjectOid(''); setProjectName(''); }} />

            <label>Sample Matrix:</label>
            <select value={matrixOid} disabled={all} onChange={e => setMatrixOid(e.target.value)}>
              <option value="">N/A</option>
              {lookups?.matrices.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>

            <label>Project ID:</label>
            <LookupField label="Project" value={projectName} disabled={all}
              onOpen={rect => setProjectPopup(rect)}
              onClear={() => { setProjectOid(''); setProjectName(''); }} />

            <label>Test:</label>
            <LookupField label="Test" value={testName} disabled={all}
              onOpen={rect => setTestPopup(rect)}
              onClear={() => { setTestOid(''); setTestName(''); }} />

            <label>All:</label>
            <input type="checkbox" className="br-all" checked={all} onChange={e => setAll(e.target.checked)} />

            <label>Sample Category:</label>
            <select value={categoryOid} disabled={all} onChange={e => setCategoryOid(e.target.value)}>
              <option value="">N/A</option>
              {lookups?.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        {/* Results card — only after a Load. */}
        {retrieved && (
          <div className="bl-card bl-card-flex">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px' }}>
              <b style={{ fontSize: 12.5 }}>▦ Batch Reporting
                <span style={{ fontWeight: 500, color: 'var(--ai-muted)', marginLeft: 6 }}>
                  ({rows.length} job{rows.length === 1 ? '' : 's'}, {tickedRows.length} selected)
                </span>
              </b>
              <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
                Right-click a Template cell to copy it to all / selected rows
              </span>
            </div>
            <AppDataGrid<BatchJobRow>
              rows={rows}
              columns={columns}
              getRowId={r => r.id}
              loading={loading}
              selectable
              selectedIds={sel}
              onSelectionChange={handleSelectionChange}
              emptyText="No jobs match the current query."
            />
          </div>
        )}
      </div>

      {ctxMenu && (
        <CellContextMenu
          value={packages.find(p => p.oid === ctxMenu.row.packageOid)?.packageName ?? ctxMenu.row.packageOid}
          columnLabel="Template"
          selectedCount={sel.size}
          position={{ x: ctxMenu.x, y: ctxMenu.y }}
          onCopyAll={() => ctxMenu.row.packageOid && copyTemplateToAll(ctxMenu.row.packageOid)}
          onCopySelected={() => ctxMenu.row.packageOid && copyTemplateToSelected(ctxMenu.row.packageOid)}
          onClose={() => setCtxMenu(null)}
        />
      )}

      {rendering && (
        <ReportProgressOverlay
          label="Generating report PDF…"
          onStop={() => renderAbortRef.current?.abort()}
        />
      )}

      {/* Client dropdown — GoLive: Client | Address | Phone */}
      {clientPopup && lookups && (
        <LookupDropdown<ClientItem>
          anchorRect={clientPopup}
          rows={lookups.clients}
          cols={[
            { label: 'Client',  width: 220, value: r => r.name },
            { label: 'Address', width: 220, value: r => r.address },
            { label: 'Phone',   width: 140, value: r => r.phone },
          ]}
          selectedId={clientOid}
          onSelect={(id, name) => {
            setClientOid(id); setClientName(name);
            setProjectOid(''); setProjectName('');
          }}
          onClose={() => setClientPopup(null)}
        />
      )}

      {/* Project dropdown — GoLive: ProjectName | ProjectID */}
      {projectPopup && lookups && (
        <LookupDropdown<ProjectItem & { name: string }>
          anchorRect={projectPopup}
          rows={projectOpts.map(p => ({ ...p, name: p.projectName || p.projectId }))}
          cols={[
            { label: 'Project Name', width: 280, value: r => r.projectName },
            { label: 'Project ID',   width: 180, value: r => r.projectId },
          ]}
          selectedId={projectOid}
          onSelect={(id, name) => { setProjectOid(id); setProjectName(name); }}
          onClose={() => setProjectPopup(null)}
        />
      )}

      {/* Test dropdown — GoLive: Test Name | Method */}
      {testPopup && lookups && (
        <LookupDropdown<TestItem>
          anchorRect={testPopup}
          rows={testOpts}
          cols={[
            { label: 'Test Name', width: 240, value: r => r.name },
            { label: 'Method',    width: 160, value: r => r.methodNumber },
          ]}
          selectedId={testOid}
          onSelect={(id, name) => { setTestOid(id); setTestName(name); }}
          onClose={() => setTestPopup(null)}
        />
      )}

      <style>{`
        .br-qform { display:grid; grid-template-columns:110px 1fr 120px 1fr; gap:10px 16px; align-items:center; }
        .br-qform > label { font-size:11.5px; color:#4a5260; font-weight:600; text-align:right; }
        .br-qform select, .br-qform input[type="date"] {
          width:100%; border:1px solid var(--ai-line); border-radius:4px; padding:5px 8px;
          font-size:11.5px; background:#fff; color:var(--ai-ink);
        }
        .br-qform select:disabled, .br-qform input:disabled { background:var(--ai-readonly-bg); color:var(--ai-muted); }
        .br-range { display:flex; align-items:center; gap:6px; min-width:0; }
        .br-range input { flex:1; min-width:0; }
        .br-arrow { color:#9aa1ad; }
        .br-all { accent-color:var(--brand-accent, var(--ai-orange)); width:16px; height:16px; }
        @media (max-width:900px){ .br-qform { grid-template-columns:110px 1fr; } }
      `}</style>
    </div>
  );
}
